import React, { useState, useEffect, useRef } from "react";
import * as S from "./styles";
import { BarChart } from "../../components/BarChart";
import { getRandomColor } from "modules/app/utils/models/colors";
import moment from "moment";
import { useMainState } from "modules/app/contexts";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exportReport } from "modules/app/utils/http/reports";
import { toast } from "react-toastify";
import fileDownload from "js-file-download";

function General() {
  const history = useHistory();
  const { user } = useMainState();
  const { t } = useTranslation(["Reports"]);
  const reportsRef = useRef();
  const [urls, setUrls] = useState({});
  const [end, setEnd] = useState(moment().format("YYYY-MM-DD"));
  const [start, setStart] = useState(
    moment().subtract(15, "days").format("YYYY-MM-DD")
  );

  function loadUrls() {
    setUrls({
      sold_products: buildUrl("/user_reports/sold_products"),
      time_to_interact: buildUrl("/user_reports/time_to_interact"),
      product_by_channels: buildUrl("/customer_reports/products_by_channel"),
      products_by_sources: buildUrl("/customer_reports/products_by_source"),
    });
  }

  function buildUrl(url) {
    return `${url}?start_at=${moment(start, "YYYY-MM-DD").format(
      "DD-MM-YYYY"
    )}&end_at=${moment(end, "YYYY-MM-DD").format("DD-MM-YYYY")}`;
  }

  async function handleExportReport(reportType) {
    const formattedStart = moment(start, "YYYY-MM-DD").format("DD/MM/YYYY");
    const formattedEnd = moment(end, "YYYY-MM-DD").format("DD/MM/YYYY");

    const filename = `${t(
      `export.${reportType}`
    )}-${formattedStart}-${formattedEnd}.csv`;

    try {
      const response = await exportReport(
        formattedStart,
        formattedEnd,
        reportType
      );
      fileDownload(response.data, filename);
    } catch (error) {
      toast.error(t("export.error_message"));
    }
  }

  useEffect(() => {
    if (user?.role == "seller") {
      history.push("/app");
    }
  }, [user]);

  useEffect(() => {
    loadUrls();
  }, []);

  return (
    <S.Container>
      <S.ReportsHeader>
        <S.Content>
          <S.Title>{t("main.title")}</S.Title>
          <S.ExportReportsButton
            onClick={() =>
              reportsRef.current.scrollIntoView({ behavior: "smooth" })
            }
          >
            {t("main.exportReportsButton")}
          </S.ExportReportsButton>
        </S.Content>

        <S.SelectDateContent>
          <S.StartAt
            label={t("main.startLabel")}
            name="start_at"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            format="DD/MM/YYYY"
          />

          <S.EndAt
            label={t("main.endLabel")}
            name="end_at"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            format="DD/MM/YYYY"
          />
          <S.UpdateButton onClick={() => loadUrls()}>
            {t("main.updateButton")}
          </S.UpdateButton>
        </S.SelectDateContent>
      </S.ReportsHeader>

      <BarChart
        title={t("charts.time_to_interact_title")}
        url={urls.time_to_interact}
        setDataConfig={function (data, _canvas) {
          return {
            labels: data.map((item) => item.name),
            datasets: [
              {
                data: data.map((item) => item.count),
                backgroundColor: data.map((item) => {
                  return getRandomColor(item.name);
                }),
              },
            ],
          };
        }}
      />

      <BarChart
        title={t("charts.sold_products_title")}
        url={urls.sold_products}
        setDataConfig={function (data, _canvas) {
          return {
            labels: data.map((item) => item.name),
            datasets: [
              {
                data: data.map((item) => item.count),
                backgroundColor: data.map((item) => {
                  return getRandomColor(item.name);
                }),
              },
            ],
          };
        }}
      />

      <BarChart
        title={t("charts.product_by_channels_title")}
        url={urls.product_by_channels}
        setDataConfig={function (data, _canvas) {
          return {
            labels: data.map((item) => item.name),
            datasets: [
              {
                data: data.map((item) => item.count),
                backgroundColor: data.map((item) => {
                  return getRandomColor(item.name);
                }),
              },
            ],
          };
        }}
      />

      <BarChart
        title={t("charts.products_by_sources_title")}
        url={urls.products_by_sources}
        setDataConfig={function (data, _canvas) {
          return {
            labels: data.filter((d) => d.count > 0).map((item) => item.name),
            datasets: [
              {
                data: data.filter((d) => d.count > 0).map((item) => item.count),
                backgroundColor: data.map((item) => {
                  return getRandomColor(item.name);
                }),
              },
            ],
          };
        }}
      />

      <S.AllReportsContainer ref={reportsRef}>
        <S.AllReportsTitle>{t("export.title")}</S.AllReportsTitle>

        <S.ReportContent>
          <div>
            <S.ReportTitle>{t("export.received_leads")}</S.ReportTitle>
            <S.ReportDescription>
              {t("export.received_leads_description")}
            </S.ReportDescription>
          </div>

          <S.ExportButton onClick={() => handleExportReport("customers_csv")}>
            {t("export.exportButton")}
            <S.DownloadIcon />
          </S.ExportButton>
        </S.ReportContent>

        <S.ReportContent>
          <div>
            <S.ReportTitle> {t("export.clients")}</S.ReportTitle>
            <S.ReportDescription>
              {t("export.clients_description")}
            </S.ReportDescription>
          </div>

          <S.ExportButton onClick={() => handleExportReport("clients_csv")}>
            {t("export.exportButton")}
            <S.DownloadIcon />
          </S.ExportButton>
        </S.ReportContent>

        <S.ReportContent>
          <div>
            <S.ReportTitle> {t("export.done_deals")}</S.ReportTitle>
            <S.ReportDescription>
              {t("export.done_deals_description")}
            </S.ReportDescription>
          </div>
          <S.ExportButton
            onClick={() => handleExportReport("sold_products_csv")}
          >
            {t("export.exportButton")}
            <S.DownloadIcon />
          </S.ExportButton>
        </S.ReportContent>
      </S.AllReportsContainer>
    </S.Container>
  );
}

export default General;
